import classNames from 'classnames';
import { Form, useFormikContext } from 'formik';
import { useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { config } from '../../../../utils/config';
import { useFormName } from '../../../../utils/formName';
import Button from '../../../atoms/Button';
import EmailField from '../../../fields/EmailField';
import GenderField from '../../../fields/GenderField';
import NamesField from '../../../fields/NamesField';
import PasswordField from '../../../fields/PasswordField';
import SecondEmailField from '../../../fields/SecondEmailField';
import { EmbeddedAuthBaseProps } from '../../../organisms/EmbeddedAuth';
import DynamicFieldArea from '../../DynamicFieldArea';
import ThirdPartySignIn from '../ThirdPartySignIn';

const Register = ({ isEmbeddedAuth }: EmbeddedAuthBaseProps) => {
  const { submitForm, isSubmitting } = useFormikContext();
  const { isLoading } = useFormName();
  const thirdPartySignInConfig = config.features.thirdPartySignIn;
  const shouldShowFirstPartySignIn = !thirdPartySignInConfig.disableFirstPartySignIn;
  const shouldShowThirdPartySignIn = thirdPartySignInConfig.enabled && !!thirdPartySignInConfig.providers.length;
  const testIdPrefix = 'register';
  const shouldShowSecondEmailField = config.features.secondEmailField.enabled;
  const firstFieldRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollFirstFieldIntoView();
  }, []);

  const scrollFirstFieldIntoView = () => {
    firstFieldRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });

    if (isEmbeddedAuth) {
      window.parent.postMessage(
        {
          type: 'scrollToElement',
          elementId: 'register-first-field',
          behavior: 'smooth',
          block: 'center'
        },
        '*'
      );
    }
  };

  return (
    <div className="option__form">
      <div className="form">
        {shouldShowFirstPartySignIn && (
          <Form>
            <div className="form__row">
              <div className="form__field-container" id="register-first-field" ref={firstFieldRef}>
                <GenderField name="gender" testIdPrefix="register" />
              </div>
            </div>
            <div className="form__row form__row--names">
              <NamesField testIdPrefix={testIdPrefix} />
            </div>
            <div className="form__row">
              <div className="form__field-container">
                <EmailField testIdPrefix={testIdPrefix} autocomplete="username" type="register" />
              </div>
            </div>
            {shouldShowSecondEmailField && (
              <div className="form__row">
                <div className="form__field-container">
                  <SecondEmailField testIdPrefix={testIdPrefix} />
                </div>
              </div>
            )}
            <div className="form__row">
              <div className="form__field-container">
                <PasswordField testIdPrefix={testIdPrefix} type="register" autocomplete="new-password" />
              </div>
            </div>
            <DynamicFieldArea area="register" />
            <div className="form__row">
              <div className="form__field-container">
                <Button
                  type="button"
                  onClick={
                    IS_WEB_COMPONENT ?
                      e => {
                        if (!isLoading) {
                          e.preventDefault();
                          submitForm();
                        }
                      }
                    : submitForm
                  }
                  data-test-id="register-submit"
                  disabled={isSubmitting}
                  className={classNames({ 'cursor-disabled': !!isLoading })}
                  isLoading={isSubmitting}
                >
                  <FormattedMessage id="auth.options.register.submit" />
                </Button>
              </div>
            </div>
          </Form>
        )}
        {shouldShowFirstPartySignIn && shouldShowThirdPartySignIn && (
          <>
            <div className="auth-step-options__divider">
              <span className="auth-step-options__divider-line auth-step-options__divider-line--left"></span>
              <FormattedMessage id="generic.or" />
              <span className="auth-step-options__divider-line auth-step-options__divider-line--right"></span>
            </div>
          </>
        )}
        {shouldShowThirdPartySignIn && (
          <ThirdPartySignIn
            isEmbeddedAuth={isEmbeddedAuth}
            providers={thirdPartySignInConfig.providers}
            testIdPrefix={testIdPrefix}
          />
        )}
      </div>
    </div>
  );
};

export default Register;
